import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, afterNextRender, computed, inject, signal, viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { timeout } from 'rxjs';
import { IconComponent } from '@kin/spark-ui';
import { SignupHeaderComponent } from '../ui/signup-header.component';
import { SignupLoaderComponent } from '../ui/signup-loader.component';
import { SignupErrorComponent } from '../errors/signup-error.component';
import { SignupResetService } from '../signup-reset.service';
import { SignupAddressStore } from '../address/signup-address.store';
import { ContactFormStore } from '../contact/contact-form.store';
import { DirectQuoteService } from '../direct-quote/direct-quote.service';
import { DirectQuoteStore } from '../direct-quote/direct-quote.store';
import { Rc1EstimateStore } from '../direct-quote/rc1-estimate.store';
import { formatCityStateZip, formatStreet } from '../../property/address.service';
import { BirthDateFieldComponent } from './birth-date-field.component';
import { Rc1EstimateCardComponent } from './rc1-estimate-card.component';

/** How long the quote CTA waits on dot-com before sending the customer to the quoting-ui funnel instead */
export const DIRECT_QUOTE_TIMEOUT_MS = 15_000;

/**
 * /enrolled: the thank-you page after an enrollment is submitted (see enrolledGuard).
 *
 * Copy is from the design file (PropertyTax - ThankYou); run it through kin-brand-messaging before this ships.
 */
@Component({
  selector: 'app-enrolled',
  imports: [
    IconComponent, SignupHeaderComponent, SignupLoaderComponent, SignupErrorComponent, BirthDateFieldComponent,
    Rc1EstimateCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: '../signup-theme.css',

  styles: `
    @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

    /* 10% margins on a phone; 72px above, so the confirmation reads first. The text keeps a short measure while
       the offer card below breaks out wider, as its own moment */
    .main {
      max-width: 760px; margin: 0 auto; padding: 72px 24px 96px; text-align: center;
      animation: fadeUp 400ms var(--kin-ease-out);
    }

    /* White check on Green-600 (5.6:1) */
    .badge {
      display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px;
      border-radius: var(--kin-radius-pill); background: var(--kin-green-600); color: var(--kin-white);
      margin-bottom: 24px;
    }
    /* Heading 1 / Body Large */
    h1 {
      font-size: 40px; font-weight: 700; line-height: 1.1; letter-spacing: -1.5px; margin: 0 0 16px;
      text-wrap: balance;
    }
    h1:focus { outline: none; }
    .lede {
      font-size: 18px; font-weight: 450; line-height: 1.7; color: var(--kin-text-secondary);
      max-width: 520px; margin: 0 auto 40px; text-wrap: pretty;
    }

    /*
     * Same card surface as /signup (24px radius), one column read top to bottom: copy, the RC1 card, then the form
     * it leads into. The card is the container the RC1 card's radius is sized from
     */
    .promo {
      display: flex; flex-direction: column; align-items: stretch; padding: 40px; text-align: left;
      container-type: inline-size;
      background: var(--kin-white); border-radius: var(--kin-radius-card); box-shadow: var(--kin-shadow-md);
    }
    /* Overline in Yellow-1000 for AA on white, as on the enroll form */
    .overline {
      font-size: 14px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
      color: var(--kin-yellow-1000); margin-bottom: 8px;
    }
    /* Heading 3 / Body */
    h2 { font-size: 22px; font-weight: 600; line-height: 1.3; margin: 0 0 12px; text-wrap: balance; }
    .promo > p {
      font-size: 16px; line-height: 1.6; color: var(--kin-text-secondary); margin: 0 0 28px; text-wrap: pretty;
    }
    .rc1 { margin: 0 0 28px; }
    /* The date of birth spans the card under the RC1 card, and the CTA keeps its own width, centered below it
       (on a phone it spans the card) */
    .quote-form { display: flex; flex-direction: column; }
    .quote-form app-birth-date-field { max-width: none; }
    .cta { align-self: center; }

    /*
     * Solid Yellow-600 CTA with Neutral-1000 text (12.20:1), like the enroll CTA. The label stays on one line
     * while the card is wide enough for it; on a phone the CTA spans the card and wraps in balanced lines.
     * Hover and press keep the colors (pinned against the page's blue link hover) and show the focus ring
     * instead; the arrow moves only in answer to hover.
     */
    .cta {
      position: relative; display: inline-flex; align-items: center; justify-content: center;
      min-height: 52px; padding: 14px 28px; box-sizing: border-box;
      font-family: inherit; font-size: 16px; font-weight: 600; line-height: 1.25; letter-spacing: -0.1px;
      color: var(--kin-neutral-1000); background: var(--kin-yellow-600);
      border: 0; border-radius: var(--kin-radius-pill); text-decoration: none; cursor: pointer;
      transition: box-shadow 120ms var(--kin-ease-out);
    }
    .cta-content { display: inline-flex; align-items: center; gap: 10px; }
    /* Starting: the loader sits over the hidden label, so the button keeps its size */
    .cta.starting { cursor: progress; }
    .cta.starting .cta-content { visibility: hidden; }
    .cta-loader { position: absolute; inset: 0; color: inherit; }
    .cta-label { white-space: nowrap; }
    .cta-icon { flex-shrink: 0; transition: transform 160ms var(--kin-ease-out); }
    .cta:hover, .cta:active { color: var(--kin-neutral-1000); box-shadow: var(--kin-shadow-focus); }
    .cta:not(.starting):hover .cta-icon { transform: translateX(3px); }
    .cta:focus-visible { outline: none; box-shadow: var(--kin-shadow-focus); }

    /* Body Small / Caption in Neutral-600 (4.6:1 on Yellow-100) */
    .questions { margin: 40px 0 0; font-size: 14px; line-height: 1.5; color: var(--kin-text-tertiary); }
    .questions a { color: var(--kin-text-secondary); font-weight: 600; }
    .footer { padding: 24px; text-align: center; font-size: 12px; color: var(--kin-text-tertiary); }

    @media (max-width: 760px) {
      .cta { align-self: stretch; }
      .cta-label { white-space: normal; text-wrap: balance; text-align: center; }
    }
    @media (max-width: 600px) {
      .main { padding: 40px 16px 56px; }
      h1 { font-size: 32px; letter-spacing: -1px; }
      .promo { padding: 24px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .main { animation: none; }
      .cta, .cta-icon { transition: none; }
      .cta:hover .cta-icon { transform: none; }
    }
  `,
  template: `
    <app-signup-header />

    <!-- The quote was asked for without the /signup answers (see startQuote): only starting over can get them -->
    @if (answersMissing()) {
      <main><app-signup-error [message]="answersMissingMessage" [restart]="true" /></main>
    } @else {
      <main class="main">
        <div class="badge" aria-hidden="true"><ngk-icon icon="check" size="32px" /></div>
        <!-- Focused on arrival (see constructor), so a screen reader starts at the confirmation -->
        <h1 #heading tabindex="-1">Thanks, you're all set.</h1>
        <p class="lede">
          We've got your info. You don't need to do anything else. If we need anything else from you, we will reach out.
        </p>

        <section class="promo" aria-labelledby="promo-title">
          <div class="overline">While you're here</div>
          <h2 id="promo-title">Curious if we can save you on home insurance too?</h2>
          <p>
            Kin is a home insurance company. Start a quick quote and see if you can save. It’s easy, with no obligation.
          </p>
          <!-- The premium when RC1 has one to show, else the benefits without a figure -->
          <app-rc1-estimate-card class="rc1" [address]="quoteAddress()" [annualPremium]="annualPremium() ?? null"
            [imageUrl]="homeImageUrl" />
          <!-- Starts a quote from the /signup answers and the date of birth (see startQuote) -->
          <form class="quote-form" novalidate aria-labelledby="promo-title" (submit)="startQuote($event)">
            <app-birth-date-field [readonly]="starting()" />
            <!-- aria-disabled, not disabled, so focus stays on the button while the quote starts -->
            <button type="submit" class="cta" [class.starting]="starting()" [attr.aria-disabled]="starting() || null">
              <span class="cta-content">
                <span class="cta-label">Get a free insurance quote</span>
                <ngk-icon class="cta-icon" icon="arrow-right" size="18px" />
              </span>
              @if (starting()) { <app-signup-loader class="cta-loader">Starting your quote...</app-signup-loader> }
            </button>
          </form>
        </section>
      </main>
    }

    <footer class="footer">
      © Kin Insurance · <a [href]="privacyUrl">Privacy</a> · <a [href]="termsUrl">Terms</a>
    </footer>
  `,

})
export class EnrolledComponent {
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private directQuote = inject(DirectQuoteService);
  private quoteStore = inject(DirectQuoteStore);
  private scroller = inject(ViewportScroller);
  private birthDateField = viewChild(BirthDateFieldComponent);

  /** From a valid submit until the browser leaves for the quote */
  protected readonly starting = signal(false);
  protected readonly answersMissing = signal(false);
  protected readonly answersMissingMessage = "We don't have your sign-up details on this page anymore. Start over to get your quote.";

  // Property tax support line, quote CTA and legal links
  readonly phoneNumber = '214-216-6542';
  readonly privacyUrl = 'https://kin.com/privacy';
  readonly termsUrl = 'https://kin.com/terms';
  readonly homeImageUrl = 'https://6rvxtlhl.media.zestyio.com/white-home.webp?auto=webp&width=800&optimize=high';

  private readonly heading = viewChild.required<ElementRef<HTMLHeadingElement>>('heading');

  private rc1 = inject(Rc1EstimateStore);
  /** The RC1 estimate the last /signup save returned; undefined: the card shows no figure */
  protected readonly annualPremium = this.rc1.annualPremium.asReadonly();
  /** e.g. "8330 Park Place Blvd, Houston, TX 77017" */
  protected readonly quoteAddress = computed(() => {
    const address = this.quoteStore.answers()?.address;
    return address ? `${formatStreet(address)}, ${formatCityStateZip(address)}` : '';
  });

  constructor() {
    // Keep the answers the direct quote starts from (memory only) before the reset below clears them
    this.quoteStore.capture(inject(SignupAddressStore).address(), inject(ContactFormStore).contact());
    this.destroyRef.onDestroy(() => this.quoteStore.reset());
    this.destroyRef.onDestroy(() => this.rc1.reset());
    // The enrollment is done: clear what this visit entered (stores and storage), as leaving /signup does
    inject(SignupResetService).reset();
    // Back from the quote restores this page from the back/forward cache mid-start: let the CTA work again
    const onPageShow = (event: PageTransitionEvent) => event.persisted && this.starting.set(false);
    this.document.defaultView?.addEventListener('pageshow', onPageShow);
    this.destroyRef.onDestroy(() => this.document.defaultView?.removeEventListener('pageshow', onPageShow));
    // Arrives from the bottom of /signup: start at the top, with focus on the confirmation
    afterNextRender(() => {
      this.scroller.scrollToPosition([0, 0]);
      this.heading().nativeElement.focus({ preventScroll: true });
    });
  }

  /**
   * Checks the date of birth (focusing it when it's missing or invalid), then saves the answers to dot-com and
   * leaves for the direct quote. Without the /signup answers (/enrolled reloaded, or reopened with Back/Forward,
   * after they were cleared) it shows the error page to start over. Any failure, or no answer within
   * DIRECT_QUOTE_TIMEOUT_MS, goes to the start of the quoting-ui funnel instead, so the CTA always leads to a quote.
   */
  protected startQuote(event: Event): void {
    event.preventDefault();
    if (this.starting()) return;
    if (!this.quoteStore.canQuote()) {
      this.answersMissing.set(true);
      this.scroller.scrollToPosition([0, 0]);
      return;
    }
    const field = this.birthDateField();
    if (!field?.commit()) {
      field?.focus();
      return;
    }

    this.starting.set(true);
    this.directQuote.createLink().pipe(timeout(DIRECT_QUOTE_TIMEOUT_MS), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: link => this.leaveFor(link),
      // The message only: a dot-com error body could echo the answers
      error: (err: unknown) => {
        console.error('Direct quote failed; going to the quoting funnel', (err as Error | null)?.message ?? err);
        this.leaveFor(this.directQuote.funnelUrl);
      },
    });
  }

  /** Same-tab navigation, via `open` so tests can spy on it (see SignupResetService.exitToLanding) */
  private leaveFor(url: string): void {
    this.document.defaultView?.open(url, '_self');
  }
}
